import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GlobalSettings } from "@/components/GlobalSettings";
import { Zap, Ticket, Heart, Gavel, ArrowRight, Sparkles } from "lucide-react";

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-[#0B0F17] text-white overflow-hidden relative grain-overlay">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full bg-rose-500/20 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -right-32 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-violet-600/10 blur-3xl" />

      <nav className="relative z-10 flex items-center justify-between px-6 py-5 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center shadow-lg shadow-rose-500/40">
            <Zap size={20} className="text-white" strokeWidth={2.8} fill="white" />
          </span>
          <span className="font-display font-black text-lg tracking-tight">
            LastMinute<span className="text-rose-400">.</span>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <GlobalSettings />
          <Link
            to="/auth"
            data-testid="landing-signin-btn"
            className="text-sm font-semibold text-slate-300 hover:text-white transition-colors"
          >
            {t("landing.signin")}
          </Link>
        </div>
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-8 pb-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6" data-testid="landing-badge">
            <Sparkles size={14} className="text-amber-400" />
            <span className="overline text-amber-400">{t("landing.eyebrow")}</span>
          </div>

          <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-6">
            {t("landing.headline_1")}
            <br />
            {t("landing.headline_2")}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-amber-400 to-violet-400">
              {t("landing.headline_3")}
            </span>
          </h1>

          <p className="text-slate-400 text-base sm:text-lg max-w-xl leading-relaxed mb-10">
            {t("landing.sub")}
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-16">
            <Link
              to="/auth"
              data-testid="landing-cta-primary"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-rose-500 text-white font-bold hover:bg-rose-400 transition-colors"
            >
              {t("landing.cta_primary")}
              <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/auth"
              data-testid="landing-cta-secondary"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 text-white font-bold hover:bg-white/5 transition-colors"
            >
              {t("landing.cta_secondary")}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Ticket, id: "tickets", color: "violet" },
            { icon: Gavel, id: "auctions", color: "amber" },
            { icon: Heart, id: "dating", color: "rose" },
            { icon: Sparkles, id: "tonight", color: "emerald" },
          ].map((f) => (
            <div
              key={f.id}
              data-testid={`landing-feature-${f.id}`}
              className="glass rounded-2xl p-5 hover:-translate-y-1 transition-transform"
            >
              <span className={`inline-flex w-10 h-10 rounded-lg items-center justify-center mb-4 bg-${f.color}-500/15 text-${f.color}-400 border border-${f.color}-500/25`}>
                <f.icon size={20} />
              </span>
              <div className={`overline mb-2 text-${f.color}-400`}>{t(`landing.features.${f.id}_tag`)}</div>
              <h3 className="font-display font-bold text-lg mb-1">{t(`landing.features.${f.id}_title`)}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{t(`landing.features.${f.id}_sub`)}</p>
            </div>
          ))}
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/5 py-6 text-center text-xs text-slate-500">
        LastMinute E-Vite · {t("landing.footer")}
      </footer>
    </div>
  );
}
